import { Options, Vue } from 'vue-class-component';
import { appRequestsService } from '@services/app-requests.service';
import { AppRequest, AppRequestStatus, AppRequestOfferStatus, PackageSize } from '@/model/AppRequest';
import { Entity } from '@/model/Entity';

import { DateFormatted } from '@sharedComponents';
import { PageHeader } from '@/components';

import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';

@Options({
  components: {
    PageHeader,
    DateFormatted,
    Dialog,
    Textarea,
    Checkbox,
    Button,
  },
})
export default class AppRequestDetail extends Vue {
  request: AppRequest | null = null;
  loading = false;
  showCancelDialog = false;
  cancelRefund = true;
  cancelMessage = '';
  cancelling = false;

  get requestId(): number {
    return parseInt(this.$route.params.id as string);
  }

  get canCancel(): boolean {
    if (!this.request || !this.request.status) return false;
    return [
      AppRequestStatus.OPEN,
      AppRequestStatus.ACCEPTED,
      AppRequestStatus.IN_PROGRESS
    ].includes(this.request.status);
  }

  async created() {
    await this.loadRequest();
  }

  async loadRequest() {
    try {
      this.loading = true;
      this.request = await appRequestsService.getById(this.requestId);
    } catch (error) {
      console.error('Error loading app request:', error);
      this.$toast.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile caricare la richiesta',
        life: 3000,
      });
    } finally {
      this.loading = false;
    }
  }

  formatAmount(amount: any): string {
    if (!amount) return '€ 0,00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '€ 0,00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(numAmount);
  }

  getStatusLabel(status?: AppRequestStatus): string {
    if (!status) return '';
    switch (status) {
      case AppRequestStatus.OPEN:
        return 'Aperto';
      case AppRequestStatus.ACCEPTED:
        return 'Accettato';
      case AppRequestStatus.IN_PROGRESS:
        return 'In Corso';
      case AppRequestStatus.DELIVERED:
        return 'Consegnato';
      case AppRequestStatus.CANCELLED:
        return 'Annullato';
      case AppRequestStatus.EXPIRED:
        return 'Scaduto';
      default:
        return status;
    }
  }

  getStatusSeverity(status?: AppRequestStatus): 'success' | 'info' | 'warning' | 'danger' {
    if (!status) return 'info';
    switch (status) {
      case AppRequestStatus.OPEN:
        return 'info';
      case AppRequestStatus.ACCEPTED:
        return 'success';
      case AppRequestStatus.IN_PROGRESS:
        return 'warning';
      case AppRequestStatus.DELIVERED:
        return 'success';
      case AppRequestStatus.CANCELLED:
        return 'danger';
      case AppRequestStatus.EXPIRED:
        return 'danger';
      default:
        return 'info';
    }
  }

  getPackageSizeLabel(size?: PackageSize): string {
    return size ? size.toUpperCase() : '';
  }

  getOfferStatusLabel(status: AppRequestOfferStatus): string {
    switch (status) {
      case AppRequestOfferStatus.PENDING:
        return 'In Attesa';
      case AppRequestOfferStatus.ACCEPTED:
        return 'Accettata';
      case AppRequestOfferStatus.REJECTED:
        return 'Rifiutata';
      default:
        return status;
    }
  }

  getOfferStatusSeverity(status: AppRequestOfferStatus): 'success' | 'info' | 'warning' | 'danger' {
    switch (status) {
      case AppRequestOfferStatus.PENDING:
        return 'warning';
      case AppRequestOfferStatus.ACCEPTED:
        return 'success';
      case AppRequestOfferStatus.REJECTED:
        return 'danger';
      default:
        return 'info';
    }
  }

  getEntityName(entity?: Entity | any): string {
    if (!entity) return '';
    
    if (entity.business_name) {
      return entity.business_name;
    }
    
    return `${entity.name || ''} ${entity.surname || ''}`.trim();
  }

  openCancelDialog() {
    this.showCancelDialog = true;
    this.cancelRefund = true;
    this.cancelMessage = '';
  }

  closeCancelDialog() {
    this.showCancelDialog = false;
    this.cancelRefund = true;
    this.cancelMessage = '';
  }

  async confirmCancel() {
    if (!this.request) return;

    try {
      this.cancelling = true;
      const updatedRequest = await appRequestsService.cancel(
        this.request.id,
        this.cancelRefund,
        this.cancelMessage
      );
      
      this.request = updatedRequest;
      
      this.$toast.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Richiesta annullata con successo',
        life: 3000,
      });
      
      this.closeCancelDialog();
    } catch (error) {
      console.error('Error cancelling request:', error);
      this.$toast.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile annullare la richiesta',
        life: 3000,
      });
    } finally {
      this.cancelling = false;
    }
  }
}
